import { useState, useRef } from 'react';
import { ReactionPicker } from './ReactionPicker';
import { RichMessageContent } from '../shared/RichMessageContent';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_username: string;
  sender_display_name: string | null;
  parent_message_id: string | null;
  content: string;
  created_at: string;
  updated_at: string;
  edited: boolean;
  last_edited_at: string | null;
  reactions?: ReactionSummary[];
}

interface ReactionSummary {
  emoji: string;
  count: number;
  user_reacted: boolean;
}

interface MessageItemProps {
  message: Message;
  currentUserId: string | undefined;
  isEditing: boolean;
  editContent: string;
  savingEdit: boolean;
  onEditContentChange: (content: string) => void;
  onStartEdit: (message: Message) => void;
  onSaveEdit: (messageId: string) => void;
  onCancelEdit: () => void;
  onDelete: (messageId: string) => void;
  onToggleReaction: (messageId: string, emoji: string, currentlyReacted: boolean) => void;
  onLoadWhoReacted: (messageId: string, emoji: string) => void;
  deleting: boolean;
  commonEmojis: string[];
}

export function MessageItem({
  message,
  currentUserId,
  isEditing,
  editContent,
  savingEdit,
  onEditContentChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onToggleReaction,
  onLoadWhoReacted,
  deleting,
  commonEmojis,
}: MessageItemProps) {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const reactionTriggerRef = useRef<HTMLButtonElement>(null);
  const isOwnMessage = message.sender_id === currentUserId;
  const displayName = message.sender_display_name || message.sender_username;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasReactions = message.reactions && message.reactions.length > 0;

  return (
    <div
      className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}
      data-testid={`message-${message.id}`}
      data-message-content={message.content}
    >
      {isEditing ? (
        // Edit mode
        <div className="max-w-[70%] w-full" data-testid={`edit-mode-${message.id}`}>
          <textarea
            value={editContent}
            onChange={(e) => onEditContentChange(e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[80px]"
            autoFocus
            disabled={savingEdit}
            data-testid={`edit-textarea-${message.id}`}
          />
          <div className="flex gap-2 mt-2">
            <Button
              onClick={() => onSaveEdit(message.id)}
              disabled={!editContent.trim() || savingEdit}
              loading={savingEdit}
              variant="primary"
              size="sm"
              data-testid={`save-edit-${message.id}`}
            >
              {savingEdit ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={onCancelEdit}
              variant="secondary"
              size="sm"
              disabled={savingEdit}
              data-testid={`cancel-edit-${message.id}`}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        // View mode
        <div className="max-w-[70%] relative group">
          <div
            className={`rounded-lg px-4 py-2 ${
              isOwnMessage ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-100'
            }`}
          >
            {/* Message actions toolbar - floated so text wraps around it */}
            <div
              className={`
                ${isOwnMessage ? 'float-right -mr-2' : 'float-left -ml-2'} -mt-0.5
                opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
                sm:opacity-0 max-sm:opacity-100
                w-0 group-hover:w-auto group-focus-within:w-auto max-sm:w-auto
                overflow-hidden group-hover:overflow-visible group-focus-within:overflow-visible max-sm:overflow-visible
                transition-opacity duration-150
                flex items-center gap-0.5
                bg-slate-800/95 backdrop-blur-sm
                border border-slate-600/50
                rounded-full shadow-lg
                p-0.5
                ${isOwnMessage ? 'ml-2' : 'mr-2'}
              `}
              data-testid={`message-actions-${message.id}`}
            >
              {/* Quick reaction trigger */}
              <div className="relative">
                <button
                  ref={reactionTriggerRef}
                  onClick={() => setShowReactionPicker(!showReactionPicker)}
                  className={`
                  w-9 h-9 sm:w-8 sm:h-8
                  flex items-center justify-center
                  rounded-full text-sm
                  hover:bg-slate-700 active:scale-95
                  transition-all duration-100 touch-manipulation
                  ${showReactionPicker ? 'bg-slate-700' : ''}
                `}
                  title="Add reaction"
                  aria-label="Add reaction"
                  aria-expanded={showReactionPicker}
                >
                  <span className="text-base">😊</span>
                </button>

                {/* Reaction Picker - positioned above the trigger */}
                <ReactionPicker
                  show={showReactionPicker}
                  commonEmojis={commonEmojis}
                  messageReactions={message.reactions}
                  position={isOwnMessage ? 'right' : 'left'}
                  triggerRef={reactionTriggerRef}
                  onSelect={(emoji, currentlyReacted) => {
                    onToggleReaction(message.id, emoji, currentlyReacted);
                    setShowReactionPicker(false);
                  }}
                  onClose={() => setShowReactionPicker(false)}
                />
              </div>

              {/* Edit/Delete buttons (only for own messages) */}
              {isOwnMessage && (
                <>
                  <div className="w-px h-4 bg-slate-600/50" />
                  <button
                    onClick={() => onStartEdit(message)}
                    className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-slate-300 hover:text-white hover:bg-slate-700 active:scale-95 transition-all duration-100 touch-manipulation"
                    title="Edit message"
                    aria-label="Edit message"
                    data-testid={`edit-message-${message.id}`}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(message.id)}
                    disabled={deleting}
                    className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-slate-300 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all duration-100 touch-manipulation"
                    title="Delete message"
                    aria-label="Delete message"
                    data-testid={`delete-message-${message.id}`}
                  >
                    {deleting ? (
                      <Spinner size="sm" variant="neutral" />
                    ) : (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    )}
                  </button>
                </>
              )}
            </div>

            {!isOwnMessage && (
              <div className="text-xs font-semibold mb-1 text-slate-300">{displayName}</div>
            )}
            <RichMessageContent content={message.content} />
            <div className={`text-xs mt-1 ${isOwnMessage ? 'text-blue-200' : 'text-slate-400'}`}>
              {formatDate(message.created_at)}
              {message.edited && ' (edited)'}
            </div>
          </div>
        </div>
      )}

      {/* Reaction badges - compact inline display */}
      {hasReactions && (
        <div className="flex gap-1 mt-1 flex-wrap items-center">
          {message.reactions!.map((reaction) => (
            <div key={reaction.emoji} className="flex items-center">
              <button
                onClick={() => onToggleReaction(message.id, reaction.emoji, reaction.user_reacted)}
                className={`
                  h-8 sm:h-7 px-2 sm:px-2
                  rounded-l-full text-sm
                  flex items-center gap-1
                  active:scale-95 transition-all duration-100 touch-manipulation
                  ${
                    reaction.user_reacted
                      ? 'bg-blue-500/25 border border-blue-400/60 text-blue-100'
                      : 'bg-slate-700/60 border border-slate-600/60 hover:bg-slate-600/60 text-slate-200'
                  }
                `}
                title="Toggle reaction"
                aria-label={`${reaction.user_reacted ? 'Remove' : 'Add'} ${reaction.emoji} reaction`}
              >
                <span className="text-sm">{reaction.emoji}</span>
              </button>
              <button
                onClick={() => onLoadWhoReacted(message.id, reaction.emoji)}
                className={`
                  h-8 sm:h-7 px-2 sm:px-2
                  rounded-r-full text-xs
                  active:scale-95 transition-all duration-100 touch-manipulation
                  border-l-0
                  ${
                    reaction.user_reacted
                      ? 'bg-blue-500/25 border border-blue-400/60 hover:bg-blue-500/35 text-blue-200'
                      : 'bg-slate-700/60 border border-slate-600/60 hover:bg-slate-600/60 text-slate-300'
                  }
                `}
                title="See who reacted"
                aria-label={`See who reacted with ${reaction.emoji}`}
              >
                {reaction.count}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
